/**
 * DemographicsStats: per-district total population, voting-age population
 * (VAP) and citizen voting-age population (CVAP), each broken down by
 * Hispanic origin and race with counts and percentages.
 *
 * Live while painting (sums over the painting store's assignments). The
 * whole-jurisdiction breakdown sits at the top as the baseline to compare
 * districts against. Groups are shown in one neutral gray, never in
 * colors, so they can't be mistaken for districts. A "Majority …" badge marks any group above 50% of the
 * selected universe — the usual first look in a Voting Rights Act review.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  computeDistrictDemographics,
  formatShare,
  UNIVERSE_LABELS,
  useDemographicsStore,
  type DemographicGroup,
  type UniverseBreakdown,
  type Universe,
} from '../../lib/demographics';
import { formatPopulation } from '../../lib/districtStats';
import { readableTextOn } from '../../lib/districtColors';
import { usePaintingStore } from '../../stores/painting';
import { useTenantStore } from '../../stores/tenant';

const UNIVERSE_KEY = 'stats:universe';

/** Compact names for the narrow panel; the full name is the tooltip. */
const SHORT_LABELS: Record<string, string> = {
  aian: 'Native American',
  nhpi: 'Pacific Islander',
};

function readUniverse(): Universe {
  try {
    const v = window.sessionStorage.getItem(UNIVERSE_KEY);
    if (v === 'pop' || v === 'vap' || v === 'cvap') return v;
  } catch {
    // ignore
  }
  return 'vap';
}

function GroupTable({ breakdown, groups }: { breakdown: UniverseBreakdown; groups: DemographicGroup[] }): JSX.Element {
  const rows = groups.filter(({ key }) => (breakdown.groups[key] ?? 0) > 0.5);
  if (breakdown.total <= 0) {
    return <p className="mt-2 text-xs text-stone-400">No blocks painted yet.</p>;
  }
  return (
    <ul className="mt-2 space-y-px text-xs">
      {rows.map(({ key, label }) => {
        const value = breakdown.groups[key] ?? 0;
        const pct = Math.min(100, (value / breakdown.total) * 100);
        return (
          <li
            key={key}
            className="grid grid-cols-[minmax(0,1fr)_auto_2.75rem] items-center gap-2 rounded px-1.5 py-0.5"
            // A neutral data bar behind the row, sized to the group's share.
            // Groups never get colors: color in this app means districts.
            style={{
              backgroundImage: `linear-gradient(to right, rgb(120 113 108 / 0.16) ${pct}%, transparent ${pct}%)`,
            }}
          >
            <span className="truncate text-stone-700" title={label}>
              {SHORT_LABELS[key] ?? label}
            </span>
            <span className="text-right tabular-nums text-stone-500">{formatPopulation(value)}</span>
            <span className="text-right font-semibold tabular-nums text-ink">
              {formatShare(value, breakdown.total)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function majorityGroup(breakdown: UniverseBreakdown, groups: DemographicGroup[]): DemographicGroup | null {
  if (breakdown.total <= 0) return null;
  return groups.find(({ key }) => (breakdown.groups[key] ?? 0) / breakdown.total > 0.5) ?? null;
}

export function DemographicsStats(): JSX.Element {
  const tenant = useTenantStore((s) => s.tenant);
  const districtCount = usePaintingStore((s) => s.districtCount);
  const colors = usePaintingStore((s) => s.colors);
  const labels = usePaintingStore((s) => s.labels);
  const activeDistrict = usePaintingStore((s) => s.activeDistrict);
  const assignments = usePaintingStore((s) => s.assignments);
  const { state, data, error, ensure } = useDemographicsStore();
  const [universe, setUniverse] = useState<Universe>(readUniverse);

  useEffect(() => {
    if (tenant) void ensure(tenant.slug);
  }, [tenant, ensure]);

  // CVAP can be absent (PL-only load): fall back to VAP.
  const effective: Universe = universe === 'cvap' && data && !data.hasCvap ? 'vap' : universe;

  const result = useMemo(
    () => (data?.available ? computeDistrictDemographics(assignments, data, districtCount) : null),
    [assignments, data, districtCount],
  );

  function choose(u: Universe): void {
    setUniverse(u);
    try {
      window.sessionStorage.setItem(UNIVERSE_KEY, u);
    } catch {
      // ignore
    }
  }

  if (state === 'loading' || state === 'idle') {
    return <p className="text-xs text-stone-500">Loading demographics…</p>;
  }
  if (state === 'error') {
    return (
      <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700" role="alert">
        Demographics couldn’t be loaded: {error}
      </p>
    );
  }
  if (!data?.available || !result) {
    return (
      <div className="rounded-xl border border-dashed border-stone-300 bg-stone-50/60 p-4 text-center">
        <p className="text-sm font-semibold text-stone-700">Demographic data isn’t loaded yet</p>
        <p className="mt-1 text-xs leading-relaxed text-stone-500">
          Voting-age, CVAP, and race figures appear here once staff load census demographics for
          this jurisdiction.
        </p>
      </div>
    );
  }

  const groups = data.groups;
  const universes: Universe[] = ['pop', 'vap', 'cvap'];

  return (
    <section aria-label="District demographics">
      <div className="sticky -top-3 z-10 -mx-4 -mt-3 mb-3 border-b border-stone-100 bg-white px-4 pb-2 pt-3">
        <div className="grid grid-cols-3 gap-0.5 rounded-lg bg-stone-100 p-0.5" role="radiogroup" aria-label="Population universe">
          {universes.map((u) => {
            const disabled = u === 'cvap' && !data.hasCvap;
            return (
              <button
                key={u}
                type="button"
                role="radio"
                aria-checked={effective === u}
                disabled={disabled}
                onClick={() => choose(u)}
                title={disabled ? 'CVAP has not been loaded for this jurisdiction' : UNIVERSE_LABELS[u].long}
                className={`rounded-md px-1.5 py-1.5 text-[11px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                  effective === u ? 'bg-white text-ink shadow-sm ring-1 ring-stone-200' : 'text-stone-500 hover:text-ink'
                }`}
              >
                {UNIVERSE_LABELS[u].short}
              </button>
            );
          })}
        </div>
        <p className="mt-1.5 text-[11px] leading-snug text-stone-500">{UNIVERSE_LABELS[effective].long}, by Hispanic origin and race.</p>
      </div>

      {data.synthetic && (
        <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-[11px] leading-snug text-amber-800">
          <span className="font-semibold">Sample data.</span> Demo City’s figures are synthetic, for
          trying the tool — not real census numbers.
        </p>
      )}

      <ul className="space-y-2">
        <li className="rounded-xl bg-stone-100/80 px-3 py-2.5">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-sm font-bold text-ink">Whole jurisdiction</span>
            <span className="text-xs font-semibold tabular-nums text-stone-600">
              {formatPopulation(result.jurisdiction[effective].total)}
            </span>
          </div>
          <GroupTable breakdown={result.jurisdiction[effective]} groups={groups} />
        </li>

        {result.districts.map((d, i) => {
          const color = colors[i] ?? '#78716c';
          const label = labels[i] ?? `District ${i + 1}`;
          const breakdown = d[effective];
          const residents = d.pop.total;
          const majority = majorityGroup(breakdown, groups);
          return (
            <li
              key={i}
              className={`rounded-xl border bg-white px-3 py-2.5 ${i === activeDistrict ? 'border-transparent shadow-sm ring-2' : 'border-stone-200'}`}
              style={i === activeDistrict ? ({ '--tw-ring-color': color } as React.CSSProperties) : undefined}
            >
              <div className="flex items-center gap-2.5">
                <span
                  aria-hidden="true"
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-[11px] font-bold shadow-sm"
                  style={{ backgroundColor: color, color: readableTextOn(color) }}
                >
                  {i + 1}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline justify-between gap-2">
                    <span className="truncate text-sm font-semibold text-ink">{label}</span>
                    <span className="shrink-0 text-xs font-semibold tabular-nums text-stone-700">
                      {formatPopulation(breakdown.total)}
                    </span>
                  </span>
                  <span className="block text-[11px] text-stone-500">
                    {effective === 'pop'
                      ? 'residents'
                      : `${UNIVERSE_LABELS[effective].short} · ${formatShare(breakdown.total, residents)} of ${formatPopulation(residents)} residents`}
                  </span>
                </span>
              </div>
              {majority && (
                <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-stone-700">
                  Majority {majority.label} {effective === 'pop' ? '' : UNIVERSE_LABELS[effective].short}
                </span>
              )}
              <GroupTable breakdown={breakdown} groups={groups} />
            </li>
          );
        })}
      </ul>

      {data.source && (
        <p className="mt-3 text-[10px] leading-snug text-stone-400">
          Source: {data.source}.{data.hasCvap && !data.synthetic ? ' CVAP block figures are estimates with ACS margins of error.' : ''}
        </p>
      )}
    </section>
  );
}
